/**
 * The **screen shake** kick for Arena juice (Arena Slice F, PRD #334, ADR-0178):
 * a tiny, capped, decaying offset the renderer adds to its camera translation on
 * the frames after a hit / elite entrance / boss beat. Pure math over its own
 * state - no Pixi, no wall clock, no randomness - so it is node-testable like
 * {@link CombatTextLayer}.
 *
 * The model is the classic **trauma** shake: kicks accumulate trauma (capped at
 * 1), trauma decays linearly on the render clock, and the applied amplitude is
 * `SHAKE_MAX_PX * trauma^2` - quadratic, so small hits barely nudge while a boss
 * entrance visibly rocks, and the tail dies off fast instead of wobbling forever.
 * The per-axis offset is a pair of incommensurate sine waves over accumulated
 * phase (deterministic - the same kicks and frame steps always replay the same
 * path, and there is no RNG to seed).
 *
 * **Hit-testing note (the acceptance criterion):** the renderer applies this
 * offset to the same `world.position` the camera already animates every frame.
 * Pixi's event boundary hit-tests through the *live* container transform, and
 * `screenToGroundTile` inverts through the *live* `world.position` too - so the
 * visuals, sprite picking, and the ground-tile inverse all read one shared
 * transform and can never disagree during a shake: a click always hits exactly
 * what is drawn under the cursor. Sim/tile coordinates are untouched (the shake
 * exists only in the camera translation), and the cap keeps the excursion under
 * {@link SHAKE_MAX_PX} pixels.
 */

/** Hard cap on the shake excursion per axis, in screen pixels (small by design). */
export const SHAKE_MAX_PX = 8;

/** Linear trauma decay per millisecond: a full-trauma kick dies out in ~450ms. */
export const SHAKE_DECAY_PER_MS = 1 / 450;

/** Kick strength when the runner takes a horde bite (the small tap). */
export const SHAKE_HIT = 0.35;

/** Kick strength on an elite entrance (a clear thud). */
export const SHAKE_ELITE = 0.6;

/** Kick strength on the boss entrance and the boss death (the full rock). */
export const SHAKE_BOSS = 1;

/** Incommensurate per-axis wave rates (radians per ms) so the path never loops. */
const WAVE_X_PER_MS = 0.055;
const WAVE_Y_PER_MS = 0.047;

export class ScreenShake {
  /** Accumulated shake energy in `[0, 1]`; amplitude is quadratic in this. */
  private trauma = 0;
  /** Accumulated phase (ms) driving the deterministic per-axis waves. */
  private phaseMs = 0;

  /** Add `strength` trauma (clamped to `[0, 1]` total); negative kicks are ignored. */
  kick(strength: number): void {
    this.trauma = Math.min(1, this.trauma + Math.max(0, strength));
  }

  /** Advance the decay + wave phase by one render frame. `dtMs <= 0` holds steady. */
  update(dtMs: number): void {
    if (dtMs <= 0) return;
    this.phaseMs += dtMs;
    this.trauma = Math.max(0, this.trauma - dtMs * SHAKE_DECAY_PER_MS);
  }

  /** Whether any shake energy remains (the renderer may skip the math when not). */
  get active(): boolean {
    return this.trauma > 0;
  }

  /** This frame's camera offset in screen pixels; exactly `{0, 0}` when spent. */
  offset(): { x: number; y: number } {
    if (this.trauma <= 0) return { x: 0, y: 0 };
    const amplitude = SHAKE_MAX_PX * this.trauma * this.trauma;
    return {
      x: amplitude * Math.sin(this.phaseMs * WAVE_X_PER_MS),
      y: amplitude * Math.cos(this.phaseMs * WAVE_Y_PER_MS),
    };
  }
}
